import WebPageLayout from "../../../../layouts/WebPageLayout";
import {Button} from "react-native-paper";
import React, {useState} from "react";
import Table from "../../../../components/Table";
import {GetWorkOrderScreenHeader} from "../work-order-screen-headers";
import {
  useGetWorkOrderLogsQuery, useGetWorkOrderQuery
} from "../../../../../../store/services/api/work-order-api";
import {useSetLoading} from "../../../../../../hooks/useSetLoading";
import {ScreenProps} from "../../../../../../utils/screen-props-helper";
import {WorkOrdersStackParamList} from "../../WorkOrdersStack";
import {WorkOrderLog} from "../../../../../../store/data/entities/work-order-log";
import {Button as AntButton, Flex} from "antd";
import {FileOutlined, EditOutlined, EyeOutlined} from "@ant-design/icons";
import UploadWorkOrderLogDocumentsModal from "../../../../../../components/modals/UploadWorkOrderLogDocumentsModal";
import AddWorkLogModal from "../../../../../../components/modals/New/AddWorkLogModal";
import {WorkOrderStatus} from "../../../../../../store/data/entities/work-order";
import useUser from "../../../../../../hooks/useUser";
import WorkLogEntryHistoryModal from "./components/WorkLogEntryHistoryModal";
import {humanizeDate} from "../../../../../../utils/humanizers";
import {Typography} from "antd";
const {Text} = Typography;

export default function WorkOrderWorkLogScreen({route}: ScreenProps<WorkOrdersStackParamList, 'workLog'>) {
  const workOrderId = route?.params?.id;
  const {data: workOrder, isFetching: workOrderFetching} = useGetWorkOrderQuery({
    id: workOrderId,
    includeAssets: false
  }, {
    skip: !workOrderId,
  });
  const {data, isFetching} = useGetWorkOrderLogsQuery(workOrderId, {
    skip: !workOrderId
  });
  const {user} = useUser();
  const [selectedWorkOrderLog, setSelectedWorkOrderLog] = useState<WorkOrderLog | undefined>(undefined);
  const [addWorkOrderLogVisible, setAddWorkOrderLogVisible] = useState(false);
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [workLogHistoryModalVisible, setWorkLogHistoryModalVisible] = useState(false);

  useSetLoading([isFetching || workOrderFetching]);
  const canEdit = user?.role === 'Admin' ||
    workOrder?.status === WorkOrderStatus.PendingApproval
    || workOrder?.status === WorkOrderStatus.Approved
    || workOrder?.status === WorkOrderStatus.Completed
    || workOrder?.status === WorkOrderStatus.InProgress;

  const openWorkLogEditModal = (item: WorkOrderLog) => {
    setSelectedWorkOrderLog(item);
    setAddWorkOrderLogVisible(true);
  }

  const openAddWorkLog = () => {
    setSelectedWorkOrderLog(undefined);
    setAddWorkOrderLogVisible(true);
  }

  const openWorkLogHistory = (item: WorkOrderLog) => {
    setSelectedWorkOrderLog(item);
    setWorkLogHistoryModalVisible(true);
  }

  const openDocumentsModal = (item: WorkOrderLog) => {
    setSelectedWorkOrderLog(item);
    setDocumentsModalVisible(true);
  }

  const closeWorkLogHistory = () => {
    setSelectedWorkOrderLog(undefined);
    setWorkLogHistoryModalVisible(false);
  }

  const closeWorkLogEditModal = () => {
    setSelectedWorkOrderLog(undefined);
    setAddWorkOrderLogVisible(false);
  }

  const closeDocumentsModal = () => {
    setSelectedWorkOrderLog(undefined);
    setDocumentsModalVisible(false);
  }

  return (
    <WebPageLayout header={GetWorkOrderScreenHeader(workOrderId)}>
      <Table<WorkOrderLog>
        keyExtractor={(_, index) => `wo-log-${index}`}
        data={data ?? []}
        columns={[
          {
            key: 'comments',
            render: (item) =>(
              <div style={{paddingBlock: 8, paddingRight: 16}}>
                <Text type='secondary'>{humanizeDate(item.createdOn, true)}</Text>
                <Text style={{display: 'block', fontSize: 16}}>{item.comments}</Text>
              </div>
            )
          },
          {
            key: 'user'
          },
          {
            render: (item) => {
              const documentCount = item.fileURIs.length;
              const showDocumentCount = documentCount > 0;
              return (
                <AntButton
                  onClick={() => openDocumentsModal(item)}
                  icon={<FileOutlined />}
                >
                  Documents{showDocumentCount && ` (${documentCount})`}
                </AntButton>
              )
            }
          },
          {
            render: (item) => {
              return (
                <Flex gap={4}>
                  <AntButton
                    disabled={!canEdit || user?.id !== item.userId}
                    onClick={() => openWorkLogEditModal(item)}
                    icon={<EditOutlined />}
                  >
                    Edit
                  </AntButton>
                  {user?.role === 'Admin' && !!item.originalLogEntryId && (
                    <AntButton
                      onClick={() => openWorkLogHistory(item)}
                      icon={<EyeOutlined />}
                    >
                      View History
                    </AntButton>
                  )}
                </Flex>
              )
            }
          }
        ]}
      />

      <Button
        disabled={!canEdit}
        style={{marginTop: 12}}
        icon='plus'
        mode="elevated"
        buttonColor='#fff'
        textColor='#0f0f0f'
        onPress={openAddWorkLog}
      >
        Add Work Log
      </Button>

      <UploadWorkOrderLogDocumentsModal
        workOrderId={workOrderId}
        workOrderLogEntryId={selectedWorkOrderLog?.originalLogEntryId ?? selectedWorkOrderLog?.id}
        existingFileURIs={selectedWorkOrderLog?.fileURIs ?? []}
        onClose={closeDocumentsModal}
        visible={documentsModalVisible}
        viewOnly={!canEdit || user?.id !== selectedWorkOrderLog?.userId}
      />

      <AddWorkLogModal
        defaultUserId={user.id}
        previousWorkOrderLog={selectedWorkOrderLog}
        open={addWorkOrderLogVisible}
        onClose={closeWorkLogEditModal}
        onSuccess={closeWorkLogEditModal}
        workOrderId={workOrderId}
      />

      <WorkLogEntryHistoryModal
        open={workLogHistoryModalVisible}
        onClose={closeWorkLogHistory}
        workOrderLog={selectedWorkOrderLog}
      />
    </WebPageLayout>
  )
}
